import { useState, useEffect } from "react";
import {
  listUsers,
  approveUser,
  revokeUser,
  getUserActivity,
  listOrganizations,
  createUser,
  deleteUser,
  editUser,
  resetUserPassword,
  createInvitation,
  listInvitations,
  resendInvitation,
  revokeInvitation,
  listJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
} from "../../api/admin";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { CheckCircle, XCircle, Eye, Plus, Copy, Trash, PencilSimple, Key, EnvelopeSimple, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatDistanceToNow, format } from "date-fns";
import { isAdminRole, isSuperAdminRole } from "../../utils/roles";
import { PERMISSION_MODULES } from "../../utils/permissions";

/**
 * AdminUsersPage - User management table
 */
export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState(null);

  // Create user modal state
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    organization_id: "",
    role: "user",
    permissions: [],
  });

  // Temporary password modal state
  const [tempPasswordOpen, setTempPasswordOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [tempPasswordContext, setTempPasswordContext] = useState("create"); // "create" | "reset"

  // Reset password confirmation dialog state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  // Delete user confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // Edit user dialog state
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editingUser, setEditingUser] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    organization_id: "",
    permissions: [],
    role: "user",
  });

  // Invite user modal state
  const [inviteUserOpen, setInviteUserOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: "",
    name: "",
    permissions: [],
  });
  const [inviteLinkOpen, setInviteLinkOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState("");

  // Invitations & Join Requests
  const [invitations, setInvitations] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);

  useEffect(() => {
    loadUsers();
    loadInvitations();
    loadJoinRequests();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      console.error("Failed to load users:", err);
      setError(err?.response?.data?.detail || "Failed to load users");
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (email) => {
    try {
      const updatedUser = await approveUser(email);
      setUsers(users.map((u) => (u.email === email ? updatedUser : u)));
      toast.success(`User ${email} approved`);
    } catch (err) {
      console.error("Failed to approve user:", err);
      toast.error(err?.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleRevoke = async (email) => {
    try {
      const updatedUser = await revokeUser(email);
      setUsers(users.map((u) => (u.email === email ? updatedUser : u)));
      toast.success(`User ${email} revoked`);
    } catch (err) {
      console.error("Failed to revoke user:", err);
      toast.error(err?.response?.data?.detail || "Failed to revoke user");
    }
  };

  const openActivityPanel = (email) => {
    setSelectedUserEmail(email);
    setActivityPanelOpen(true);
  };

  // ── Invitations & Join Requests ──────────────────────────────────────────

  const loadInvitations = async () => {
    try {
      const data = await listInvitations();
      setInvitations(data.filter((i) => i.status === "pending"));
    } catch (err) {
      // Non-fatal — invitations panel just won't show
    }
  };

  const loadJoinRequests = async () => {
    try {
      const data = await listJoinRequests();
      setJoinRequests(data.filter((jr) => jr.status === "pending"));
    } catch (err) {
      // Non-fatal
    }
  };

  const openInviteUserModal = () => {
    setInviteFormData({ email: "", name: "", permissions: [] });
    setInviteUserOpen(true);
  };

  const handleInvitePermissionToggle = (permissionKey) => {
    setInviteFormData((prev) => {
      const newPermissions = prev.permissions.includes(permissionKey)
        ? prev.permissions.filter((p) => p !== permissionKey)
        : [...prev.permissions, permissionKey];
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleInviteUser = async () => {
    if (!inviteFormData.email) {
      toast.error("Email is required");
      return;
    }
    setInviting(true);
    try {
      const payload = { email: inviteFormData.email, permissions: inviteFormData.permissions };
      if (inviteFormData.name) payload.name = inviteFormData.name;

      const result = await createInvitation(payload);
      setInviteUserOpen(false);

      if (result.invite_url) {
        // Email not sent — show the link for manual sharing
        setInviteLink(result.invite_url);
        setInviteLinkOpen(true);
      } else {
        toast.success(`Invitation sent to ${inviteFormData.email}`);
      }

      await loadInvitations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (id) => {
    try {
      const result = await resendInvitation(id);
      if (result.invite_url) {
        setInviteLink(result.invite_url);
        setInviteLinkOpen(true);
      } else {
        toast.success("Invitation resent");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const handleRevokeInvitation = async (id) => {
    try {
      await revokeInvitation(id);
      setInvitations(invitations.filter((i) => i.id !== id));
      toast.success("Invitation revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke invitation");
    }
  };

  const handleApproveJoinRequest = async (id) => {
    try {
      await approveJoinRequest(id);
      setJoinRequests(joinRequests.filter((jr) => jr.id !== id));
      await loadUsers();
      toast.success("Join request approved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve join request");
    }
  };

  const handleRejectJoinRequest = async (id) => {
    try {
      await rejectJoinRequest(id);
      setJoinRequests(joinRequests.filter((jr) => jr.id !== id));
      toast.success("Join request rejected");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reject join request");
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const openCreateUserModal = async () => {
    // Load organizations if super_admin
    if (currentUser?.role === "super_admin" || currentUser?.role === "admin") {
      try {
        const orgs = await listOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        console.error("Failed to load organizations:", err);
        toast.error("Failed to load organizations");
      }
    }
    setCreateUserOpen(true);
  };

  const closeCreateUserModal = () => {
    setCreateUserOpen(false);
    // Reset form
    setFormData({
      email: "",
      name: "",
      organization_id: "",
      role: "user",
      permissions: [],
    });
  };

  const handlePermissionToggle = (permissionKey) => {
    setFormData((prev) => {
      const newPermissions = prev.permissions.includes(permissionKey)
        ? prev.permissions.filter((p) => p !== permissionKey)
        : [...prev.permissions, permissionKey];
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleToggleSelectAllPermissions = () => {
    setFormData((prev) => {
      const allSelected = prev.permissions.length === PERMISSION_MODULES.length;
      return { ...prev, permissions: allSelected ? [] : PERMISSION_MODULES.map((m) => m.key) };
    });
  };

  const handleCreateUser = async () => {
    // Validate email
    if (!formData.email) {
      toast.error("Email is required");
      return;
    }

    setCreatingUser(true);
    try {
      // Build payload based on user role
      const payload = {
        email: formData.email,
        permissions: formData.permissions,
      };

      if (formData.name) {
        payload.name = formData.name;
      }

      // Only super_admin can set organization and role
      if (currentUser?.role === "super_admin" || currentUser?.role === "admin") {
        if (formData.organization_id) {
          payload.organization_id = formData.organization_id;
        }
        payload.role = formData.role;
      }

      const result = await createUser(payload);

      // Close create modal
      closeCreateUserModal();

      // Show temporary password modal
      setTempPassword(result.temporary_password);
      setTempPasswordOpen(true);

      // Refresh users list
      await loadUsers();

      toast.success("User created successfully");
    } catch (err) {
      console.error("Failed to create user:", err);
      toast.error(err?.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const copyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      toast.success("Password copied to clipboard");
    } catch (err) {
      console.error("Failed to copy password:", err);
      toast.error("Failed to copy password");
    }
  };

  const closeTempPasswordModal = () => {
    setTempPasswordOpen(false);
    setTempPassword("");
    setTempPasswordContext("create");
  };

  const openResetPasswordDialog = (user) => {
    setUserToResetPassword(user);
    setResetPasswordDialogOpen(true);
  };

  const closeResetPasswordDialog = () => {
    if (!resettingPassword) {
      setResetPasswordDialogOpen(false);
      setUserToResetPassword(null);
    }
  };

  const handleConfirmResetPassword = async () => {
    if (!userToResetPassword) return;

    setResettingPassword(true);
    try {
      const result = await resetUserPassword(userToResetPassword.email);

      setResetPasswordDialogOpen(false);
      setUserToResetPassword(null);

      // Reveal the generated password using the same dialog as Create User.
      setTempPasswordContext("reset");
      setTempPassword(result.temporary_password);
      setTempPasswordOpen(true);
    } catch (err) {
      console.error("Failed to reset password:", err);
      toast.error(err?.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (!deletingUser) {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      await deleteUser(userToDelete.email);
      
      // Remove user from local state
      setUsers(users.filter((u) => u.email !== userToDelete.email));
      
      toast.success(`User ${userToDelete.email} deleted successfully`);
      closeDeleteDialog();
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error(err?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUser(false);
    }
  };

  const openEditDialog = async (user) => {
    // Load organizations if super_admin
    if (isSuperAdmin) {
      try {
        const orgs = await listOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        console.error("Failed to load organizations:", err);
        toast.error("Failed to load organizations");
      }
    }

    setUserToEdit(user);
    setEditFormData({
      name: user.name || "",
      organization_id: user.organization_id || "",
      permissions: user.permissions || [],
      role: user.role || "user",
    });
    setEditUserOpen(true);
  };

  const closeEditDialog = () => {
    if (!editingUser) {
      setEditUserOpen(false);
      setUserToEdit(null);
      setEditFormData({
        name: "",
        organization_id: "",
        permissions: [],
        role: "user",
      });
    }
  };

  const handleEditPermissionToggle = (permissionKey) => {
    setEditFormData((prev) => {
      const newPermissions = prev.permissions.includes(permissionKey)
        ? prev.permissions.filter((p) => p !== permissionKey)
        : [...prev.permissions, permissionKey];
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleToggleSelectAllEditPermissions = () => {
    setEditFormData((prev) => {
      const allSelected = prev.permissions.length === PERMISSION_MODULES.length;
      return { ...prev, permissions: allSelected ? [] : PERMISSION_MODULES.map((m) => m.key) };
    });
  };

  const handleEditUser = async () => {
    if (!userToEdit) return;

    setEditingUser(true);
    try {
      // Build payload
      const payload = {
        name: editFormData.name,
        permissions: editFormData.permissions,
      };

      // Only include organization_id if super_admin
      if (isSuperAdmin && editFormData.organization_id) {
        payload.organization_id = editFormData.organization_id;
      }

      // Only include role if super_admin (org_admin cannot promote/demote)
      if (isSuperAdmin && editFormData.role) {
        payload.role = editFormData.role;
      }

      const updatedUser = await editUser(userToEdit.email, payload);

      // Update user in local state
      setUsers(users.map((u) => (u.email === userToEdit.email ? updatedUser : u)));

      toast.success("User updated successfully");
      closeEditDialog();
    } catch (err) {
      console.error("Failed to update user:", err);
      toast.error(err?.response?.data?.detail || "Failed to update user");
    } finally {
      setEditingUser(false);
    }
  };

  const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.role === "admin";

  if (loading) {
    return (
      <div>
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-6">
          Users
        </h2>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-6">
          Users
        </h2>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900">
          Users
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={openInviteUserModal}
            variant="outline"
            data-testid="invite-user-button"
            className="rounded-sm"
          >
            <EnvelopeSimple size={16} className="mr-1" />
            Invite User
          </Button>
          <Button
            onClick={openCreateUserModal}
            data-testid="create-user-button"
            className="rounded-sm"
          >
            <Plus size={16} className="mr-1" />
            Create User
          </Button>
        </div>
      </div>

      {/* Join Requests Section */}
      {joinRequests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 rounded-sm mb-4" data-testid="join-requests-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <UserPlus size={16} />
              Pending Join Requests ({joinRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {joinRequests.map((jr) => (
                <div key={jr.id} className="flex flex-col gap-3 bg-white border border-amber-200 rounded-sm px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <span className="font-mono text-sm text-slate-800">{jr.user_email}</span>
                    <span className="text-xs text-slate-500 ml-3">
                      Requested {jr.requested_at ? formatTimestamp(jr.requested_at) : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleApproveJoinRequest(jr.id)}
                      className="rounded-sm text-xs bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleRejectJoinRequest(jr.id)}
                      className="rounded-sm text-xs"
                    >
                      <XCircle size={14} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card className="border-blue-200 bg-blue-50 rounded-sm mb-4" data-testid="invitations-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <EnvelopeSimple size={16} />
              Pending Invitations ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex flex-col gap-3 bg-white border border-blue-200 rounded-sm px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <span className="font-mono text-sm text-slate-800">{inv.email}</span>
                    {inv.name && <span className="text-sm text-slate-600 ml-2">({inv.name})</span>}
                    <span className="text-xs text-slate-500 ml-3">
                      Invited {inv.created_at ? formatTimestamp(inv.created_at) : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResendInvitation(inv.id)}
                      className="rounded-sm text-xs"
                    >
                      Resend
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRevokeInvitation(inv.id)}
                      className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="min-w-0 overflow-hidden border-slate-200 rounded-sm">
        <CardContent className="min-w-0 p-0">
          {users.length === 0 ? (
            <div className="p-8 text-center text-slate-500 italic">
              No users yet
            </div>
          ) : (
            <Table
              className="min-w-[960px]"
              containerTestId="admin-users-table-scroll"
              data-testid="admin-users-table"
            >
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Role</TableHead>
                  <TableHead className="font-semibold">Organization</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Tokens</TableHead>
                  <TableHead className="font-semibold">Last Login</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.email} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={isAdminRole(u.role) ? "default" : "secondary"}
                        className="rounded-sm"
                      >
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {u.organization_name || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={u.status === "active" ? "default" : "destructive"}
                        className={`rounded-sm ${
                          u.status === "active"
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-red-600 hover:bg-red-700"
                        }`}
                      >
                        {u.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {u.total_tokens?.toLocaleString() || "0"}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {u.last_login_at
                        ? formatTimestamp(u.last_login_at)
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {u.status === "revoked" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(u.email)}
                            data-testid={`approve-btn-${u.email}`}
                            className="rounded-sm text-xs"
                          >
                            <CheckCircle size={14} />
                            Approve
                          </Button>
                        )}
                        {u.status === "active" && !isSuperAdminRole(u.role) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRevoke(u.email)}
                            disabled={u.email === currentUser?.email}
                            data-testid={`revoke-btn-${u.email}`}
                            className="rounded-sm text-xs"
                          >
                            <XCircle size={14} />
                            Revoke
                          </Button>
                        )}
                        {!isSuperAdminRole(u.role) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(u)}
                            data-testid={`edit-btn-${u.email}`}
                            className="rounded-sm text-xs"
                          >
                            <PencilSimple size={14} />
                          </Button>
                        )}
                        {!isSuperAdminRole(u.role) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openResetPasswordDialog(u)}
                            data-testid={`reset-password-btn-${u.email}`}
                            className="rounded-sm text-xs"
                            title="Reset Password"
                          >
                            <Key size={14} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openActivityPanel(u.email)}
                          data-testid={`view-activity-btn-${u.email}`}
                          className="rounded-sm text-xs"
                        >
                          <Eye size={14} />
                          View Activity
                        </Button>
                        {!isSuperAdminRole(u.role) && u.email !== currentUser?.email && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDeleteDialog(u)}
                            data-testid={`delete-btn-${u.email}`}
                            className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Activity Panel */}
      <Sheet open={activityPanelOpen} onOpenChange={setActivityPanelOpen}>
        <SheetContent className="sm:max-w-xl" data-testid="user-activity-panel">
          <SheetHeader>
            <SheetTitle className="font-cabinet text-xl font-bold">
              User Activity
            </SheetTitle>
          </SheetHeader>
          {selectedUserEmail && (
            <ActivityPanel email={selectedUserEmail} />
          )}
        </SheetContent>
      </Sheet>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0" data-testid="create-user-dialog">
          <div className="shrink-0 px-6 pt-6">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-bold">
                Create New User
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="overflow-y-auto flex-1 px-6 space-y-4 py-4">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="create-user-email-input"
                className="rounded-sm"
              />
            </div>

            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-semibold text-slate-700">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="create-user-name-input"
                className="rounded-sm"
              />
            </div>

            {/* Organization Select - Only for super_admin */}
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="organization" className="text-sm font-semibold text-slate-700">
                  Organization
                </Label>
                <Select
                  value={formData.organization_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, organization_id: value })
                  }
                >
                  <SelectTrigger
                    id="organization"
                    data-testid="create-user-org-select"
                    className="rounded-sm"
                  >
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Role Select - Only for super_admin */}
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm font-semibold text-slate-700">
                  Role
                </Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger
                    id="role"
                    data-testid="create-user-role-select"
                    className="rounded-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="org_admin">Organization Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Permissions Checkboxes */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Permissions
              </Label>
              <div className="space-y-3 border border-slate-200 rounded-sm p-3">
                <div className="flex items-center space-x-2 pb-2 border-b border-slate-200">
                  <Checkbox
                    id="permission-select-all"
                    checked={formData.permissions.length === PERMISSION_MODULES.length}
                    onCheckedChange={handleToggleSelectAllPermissions}
                    data-testid="create-user-permission-select-all"
                  />
                  <Label
                    htmlFor="permission-select-all"
                    className="text-sm font-semibold cursor-pointer"
                  >
                    Select All
                  </Label>
                </div>
                {PERMISSION_MODULES.map((module) => (
                  <div key={module.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`permission-${module.key}`}
                      checked={formData.permissions.includes(module.key)}
                      onCheckedChange={() => handlePermissionToggle(module.key)}
                      data-testid={`create-user-permission-${module.key}`}
                    />
                    <Label
                      htmlFor={`permission-${module.key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {module.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="shrink-0 px-6 pb-6 pt-4 border-t border-slate-200">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={closeCreateUserModal}
                disabled={creatingUser}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={creatingUser}
                data-testid="create-user-submit-btn"
                className="rounded-sm"
              >
                {creatingUser ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Temporary Password Dialog */}
      <Dialog open={tempPasswordOpen} onOpenChange={setTempPasswordOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0 gap-0" data-testid="temporary-password-dialog">
          <div className="shrink-0 px-6 pt-6">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-bold">
                {tempPasswordContext === "reset" ? "Password Reset Successfully" : "User Created Successfully"}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="overflow-y-auto flex-1 px-6 space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
              <p className="text-sm text-yellow-800 font-semibold mb-2">
                ⚠️ Important: This password will not be shown again
              </p>
              <p className="text-xs text-yellow-700">
                Please copy this temporary password and share it with the user securely.
                They will be required to change it on first login.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Temporary Password
              </Label>
              <div className="flex items-center gap-2">
                <div
                  data-testid="temporary-password-value"
                  className="flex-1 bg-slate-100 border border-slate-200 rounded-sm p-3 font-mono text-lg font-bold text-slate-900 break-all"
                >
                  {tempPassword}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyTempPassword}
                  data-testid="temporary-password-copy-btn"
                  className="rounded-sm shrink-0"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>
          </div>

          <div className="shrink-0 px-6 pb-6 pt-4 border-t border-slate-200">
            <DialogFooter>
              <Button
                onClick={closeTempPasswordModal}
                data-testid="temporary-password-done-btn"
                className="rounded-sm w-full"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={closeResetPasswordDialog}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-bold">
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-700">
              This will generate a new temporary password for{" "}
              <span className="font-semibold font-mono">{userToResetPassword?.email}</span>{" "}
              and immediately invalidate their current one. They will be required to set a
              new permanent password the next time they log in.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeResetPasswordDialog}
              disabled={resettingPassword}
              data-testid="reset-password-cancel-btn"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmResetPassword}
              disabled={resettingPassword}
              data-testid="reset-password-confirm-btn"
              className="rounded-sm"
            >
              {resettingPassword ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={closeDeleteDialog}>
        <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-bold">
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-700">
              Are you sure you want to permanently delete <span className="font-semibold font-mono">{userToDelete?.email}</span>? This will delete all of their projects, drawings, automations, notifications, and other associated data. This action CANNOT be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deletingUser}
              data-testid="cancel-delete-btn"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletingUser}
              data-testid="confirm-delete-btn"
              className="rounded-sm"
            >
              {deletingUser ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={closeEditDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0" data-testid="edit-user-dialog">
          <div className="shrink-0 px-6 pt-6">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-bold">
                Edit User
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="overflow-y-auto flex-1 px-6 space-y-4 py-4">
            {/* Email Input (Disabled) */}
            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-sm font-semibold text-slate-700">
                Email
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={userToEdit?.email || ""}
                disabled
                data-testid="edit-user-email-input"
                className="rounded-sm bg-slate-50"
              />
              <p className="text-xs text-slate-500">Email cannot be changed.</p>
            </div>

            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-sm font-semibold text-slate-700">
                Name
              </Label>
              <Input
                id="edit-name"
                type="text"
                placeholder="John Doe"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                data-testid="edit-user-name-input"
                className="rounded-sm"
              />
            </div>

            {/* Role Select - Only for super_admin. Promote/demote between User <-> Org Admin. */}
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="edit-role" className="text-sm font-semibold text-slate-700">
                  Role
                </Label>
                <Select
                  value={editFormData.role}
                  onValueChange={(value) =>
                    setEditFormData({ ...editFormData, role: value })
                  }
                >
                  <SelectTrigger
                    id="edit-role"
                    data-testid="edit-user-role-select"
                    className="rounded-sm"
                  >
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="org_admin">Organization Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Organization Select - Only for super_admin */}
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="edit-organization" className="text-sm font-semibold text-slate-700">
                  Organization
                </Label>
                <Select
                  value={editFormData.organization_id}
                  onValueChange={(value) =>
                    setEditFormData({ ...editFormData, organization_id: value })
                  }
                >
                  <SelectTrigger
                    id="edit-organization"
                    data-testid="edit-user-org-select"
                    className="rounded-sm"
                  >
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Permissions Checkboxes */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Permissions
              </Label>
              <div className="space-y-3 border border-slate-200 rounded-sm p-3">
                <div className="flex items-center space-x-2 pb-2 border-b border-slate-200">
                  <Checkbox
                    id="edit-permission-select-all"
                    checked={editFormData.permissions.length === PERMISSION_MODULES.length}
                    onCheckedChange={handleToggleSelectAllEditPermissions}
                    data-testid="edit-user-permission-select-all"
                  />
                  <Label
                    htmlFor="edit-permission-select-all"
                    className="text-sm font-semibold cursor-pointer"
                  >
                    Select All
                  </Label>
                </div>
                {PERMISSION_MODULES.map((module) => (
                  <div key={module.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-permission-${module.key}`}
                      checked={editFormData.permissions.includes(module.key)}
                      onCheckedChange={() => handleEditPermissionToggle(module.key)}
                      data-testid={`edit-user-permission-${module.key}`}
                    />
                    <Label
                      htmlFor={`edit-permission-${module.key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {module.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="shrink-0 px-6 pb-6 pt-4 border-t border-slate-200">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={closeEditDialog}
                disabled={editingUser}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditUser}
                disabled={editingUser}
                data-testid="edit-user-submit-btn"
                className="rounded-sm"
              >
                {editingUser ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteUserOpen} onOpenChange={setInviteUserOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0" data-testid="invite-user-dialog">
          <div className="shrink-0 px-6 pt-6">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-bold">
                Invite User
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="overflow-y-auto flex-1 px-6 space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="text-sm font-semibold text-slate-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="user@example.com"
                value={inviteFormData.email}
                onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                data-testid="invite-user-email-input"
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-name" className="text-sm font-semibold text-slate-700">
                Name (optional)
              </Label>
              <Input
                id="invite-name"
                type="text"
                placeholder="John Doe"
                value={inviteFormData.name}
                onChange={(e) => setInviteFormData({ ...inviteFormData, name: e.target.value })}
                data-testid="invite-user-name-input"
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Permissions</Label>
              <div className="space-y-3 border border-slate-200 rounded-sm p-3">
                {PERMISSION_MODULES.map((module) => (
                  <div key={module.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`invite-permission-${module.key}`}
                      checked={inviteFormData.permissions.includes(module.key)}
                      onCheckedChange={() => handleInvitePermissionToggle(module.key)}
                      data-testid={`invite-user-permission-${module.key}`}
                    />
                    <Label
                      htmlFor={`invite-permission-${module.key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {module.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="shrink-0 px-6 pb-6 pt-4 border-t border-slate-200">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setInviteUserOpen(false)}
                disabled={inviting}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteUser}
                disabled={inviting}
                data-testid="invite-user-submit-btn"
                className="rounded-sm"
              >
                {inviting ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Link Fallback Dialog */}
      <Dialog open={inviteLinkOpen} onOpenChange={setInviteLinkOpen}>
        <DialogContent className="sm:max-w-md" data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-bold">
              Invite Link
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
              <p className="text-sm text-blue-800">
                Email could not be sent automatically. Share this link with the invitee manually:
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-100 border border-slate-200 rounded-sm p-3 font-mono text-xs break-all">
                {inviteLink}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={copyInviteLink}
                className="rounded-sm shrink-0"
              >
                <Copy size={16} />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setInviteLinkOpen(false)} className="rounded-sm w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActivityPanel({ email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadActivity();
  }, [email]);

  const loadActivity = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUserActivity(email);
      setData(result);
    } catch (err) {
      console.error("Failed to load user activity:", err);
      setError(err?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-6 space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm text-sm">
        {error}
      </div>
    );
  }

  const { user: userInfo, usage, recent_actions } = data;

  return (
    <div className="mt-6 space-y-6">
      {/* User Info */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-slate-600">
            User Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Email:</span>
            <span className="font-mono text-slate-900">{userInfo.email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Name:</span>
            <span className="font-medium text-slate-900">{userInfo.name || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Role:</span>
            <Badge
              variant={isAdminRole(userInfo.role) ? "default" : "secondary"}
              className="rounded-sm"
            >
              {userInfo.role}
            </Badge>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Status:</span>
            <Badge
              variant={userInfo.status === "active" ? "default" : "destructive"}
              className={`rounded-sm ${
                userInfo.status === "active"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {userInfo.status}
            </Badge>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Created:</span>
            <span className="text-slate-900">
              {format(new Date(userInfo.created_at), "MMM d, yyyy")}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Last Login:</span>
            <span className="text-slate-900">
              {userInfo.last_login_at
                ? format(new Date(userInfo.last_login_at), "MMM d, yyyy HH:mm")
                : "Never"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-slate-600">
            Usage Statistics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Total Tokens:</span>
            <span className="font-mono font-semibold text-slate-900">
              {usage.total_tokens?.toLocaleString() || "0"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Input Tokens:</span>
            <span className="font-mono text-slate-900">
              {usage.total_input?.toLocaleString() || "0"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Output Tokens:</span>
            <span className="font-mono text-slate-900">
              {usage.total_output?.toLocaleString() || "0"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">LLM Calls:</span>
            <span className="font-mono text-slate-900">
              {usage.total_calls?.toLocaleString() || "0"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Recent Actions */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-slate-600">
            Recent Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!recent_actions || recent_actions.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No recent actions</p>
          ) : (
            <div className="space-y-3">
              {recent_actions.slice(0, 10).map((action) => (
                <div
                  key={action.id}
                  className="pb-3 border-b border-slate-100 last:border-0 last:pb-0"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-semibold text-sm text-slate-900">
                      {action.action}
                    </span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {formatTimestamp(action.timestamp)}
                    </span>
                  </div>
                  {action.detail && (
                    <p className="text-xs text-slate-600">{action.detail}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatTimestamp(timestamp) {
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch {
    return timestamp;
  }
}
